import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Nav } from "react-bootstrap";
import {
  ArrowLeft,
  Edit,
  Download,
  FileSpreadsheet,
  Send,
  Layers,
  Sliders,
  FileText,
  Search as SearchIcon,
  CheckCircle2,
  Boxes,
  Compass,
  DollarSign
} from "lucide-react";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import moment from "moment";

import Header from "../Include/Header";
import Sidebar from "../Include/Sidebar";
import Footer from "../Include/Footer";
import Loader from "../Include/Loader";

import { getAdminOneBom } from "../../../Store/Admin/believe/bom/GetOneBom";
import { downloadAdminBomPdf } from "../../../Store/Admin/believe/bom/DownloadBomPdf";
import { exportAdminBomExcel } from "../../../Store/Admin/believe/bom/ExportBomExcel";
import { generatePrFromAdminBom } from "../../../Store/Admin/believe/bom/GeneratePrBom";
import { formatINR, numberToWords } from "../../../helper/bom";

const ReadOnlyInput = ({ label, value, colClass = "col-12 col-md-4 col-xl-3" }) => (
  <div className={colClass}>
    <div className="input-block local-forms custom-select-wpr">
      <label>{label}</label>
      <input className="form-control" value={value || "-"} readOnly />
    </div>
  </div>
);

const ViewBom = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("offer");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (localStorage.getItem("VA_TOKEN") === null) {
      navigate("/admin/login");
    }
  }, [navigate]);

  useEffect(() => {
    if (id) {
      dispatch(getAdminOneBom(id));
    }
  }, [dispatch, id]);

  const { data: bomResponse, loading } = useSelector((state) => state?.getAdminOneBom || {});
  const bom = bomResponse?.data || bomResponse;

  const handleDownloadPdf = () => {
    if (!bom?._id) return;
    toast.loading("Generating PDF...", { id: "pdf-view-toast" });
    dispatch(downloadAdminBomPdf(bom._id)).then((res) => {
      toast.dismiss("pdf-view-toast");
      if (res?.payload?.success && res?.payload?.data?.url) {
        window.open(res.payload.data.url, "_blank");
      }
    });
  };

  const handleExportExcel = () => {
    if (!bom?._id) return;
    toast.loading("Exporting Excel...", { id: "excel-view-toast" });
    dispatch(exportAdminBomExcel(bom._id)).then((res) => {
      toast.dismiss("excel-view-toast");
      if (res?.payload?.success && res?.payload?.data?.url) {
        window.open(res.payload.data.url, "_blank");
      }
    });
  };

  const handleGeneratePr = () => {
    if (!bom?._id) return;
    Swal.fire({
      title: "Generate Store PR?",
      text: `Create Store Purchase Request for BOM ${bom.bom_no}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Generate PR",
      cancelButtonText: "Cancel"
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(generatePrFromAdminBom(bom._id)).then((res) => {
          if (res?.payload?.success) {
            toast.success("Purchase Request generated successfully!");
            dispatch(getAdminOneBom(bom._id));
          }
        });
      }
    });
  };

  // Metrics computation
  const metrics = useMemo(() => {
    if (!bom?.assemblies) {
      return { totalAssemblies: 0, totalFeeders: 0, totalItems: 0 };
    }
    let feeders = 0;
    let items = 0;
    bom.assemblies.forEach((ass) => {
      feeders += (ass.sub_assemblies || []).length;
      (ass.sub_assemblies || []).forEach((sub) => {
        items += (sub.items || []).length;
      });
    });
    return {
      totalAssemblies: bom.assemblies.length,
      totalFeeders: feeders,
      totalItems: items
    };
  }, [bom]);

  // Filtered assemblies for Tab 2 search
  const filteredAssemblies = useMemo(() => {
    if (!bom?.assemblies) return [];
    if (!searchTerm.trim()) return bom.assemblies;

    const term = searchTerm.toLowerCase();
    return bom.assemblies.map((ass) => {
      const matchesAssName = ass.assembly_name?.toLowerCase().includes(term);
      const filteredSubs = (ass.sub_assemblies || []).map((sub) => {
        const matchesSubName = sub.name?.toLowerCase().includes(term) || sub.code?.toLowerCase().includes(term);
        const filteredItems = (sub.items || []).filter((item) =>
          item.item_name?.toLowerCase().includes(term) ||
          item.description?.toLowerCase().includes(term) ||
          item.make?.toLowerCase().includes(term)
        );
        if (matchesSubName || filteredItems.length > 0) {
          return {
            ...sub,
            items: matchesSubName && filteredItems.length === 0 ? sub.items : filteredItems
          };
        }
        return null;
      }).filter(Boolean);

      if (matchesAssName || filteredSubs.length > 0) {
        return {
          ...ass,
          sub_assemblies: matchesAssName && filteredSubs.length === 0 ? ass.sub_assemblies : filteredSubs
        };
      }
      return null;
    }).filter(Boolean);
  }, [bom?.assemblies, searchTerm]);

  if (loading || !bom) {
    return (
      <div className="main-wrapper">
        <Header handleOpen={() => setIsSidebarOpen(!isSidebarOpen)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content">
            <Loader />
          </div>
        </div>
      </div>
    );
  }

  const clientAddress = [
    bom.client_snapshot?.address,
    bom.client_snapshot?.city,
    bom.client_snapshot?.state,
    bom.client_snapshot?.pincode
  ].filter(Boolean).join(", ");

  const grandTotalInWords = numberToWords(bom.grand_total || 0);

  return (
    <div className={`main-wrapper ${isSidebarOpen ? "slide-nav" : ""}`}>
      <Header handleOpen={() => setIsSidebarOpen(!isSidebarOpen)} />
      <Sidebar />

      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb Header */}
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col-sm-6 col-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/admin/dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right"></i>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/admin/bom-management">Bill of Materials</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right"></i>
                  </li>
                  <li className="breadcrumb-item active">View BOM Details</li>
                </ul>
              </div>
              <div className="col-sm-6 col-12 text-end d-flex justify-content-end align-items-center gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                  onClick={() => navigate("/admin/bom-management")}
                >
                  <ArrowLeft size={14} /> Back to List
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                  onClick={() => navigate("/admin/manage-bom", { state: { id: bom._id } })}
                >
                  <Edit size={14} /> Edit BOM
                </button>
                <button
                  type="button"
                  className="btn btn-outline-dark btn-sm d-flex align-items-center gap-1"
                  onClick={handleDownloadPdf}
                >
                  <Download size={14} /> PDF
                </button>
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm d-flex align-items-center gap-1"
                  onClick={handleExportExcel}
                >
                  <FileSpreadsheet size={14} /> Excel
                </button>
                {!bom.pr_generated && (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                    onClick={handleGeneratePr}
                  >
                    <Send size={14} /> Generate PR
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* BOM Overview / Master Details Card */}
          <div className="row">
            <div className="col-sm-12">
              <div className="card mb-3">
                <div className="card-body">
                  <div className="col-12">
                    <div className="form-heading d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                      <h4 className="mb-0">
                        View Bill of Material Details: <span className="text-primary">{bom.bom_no}</span>
                      </h4>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        {bom.version > 0 && (
                          <span className="badge bg-secondary" style={{ fontSize: "11px" }}>
                            Rev {bom.version}
                          </span>
                        )}
                        <span
                          className={`custom-badge ${
                            bom.status === "Approved"
                              ? "status-green"
                              : bom.status === "Submitted"
                              ? "status-blue"
                              : bom.status === "Rejected"
                              ? "status-pink"
                              : "status-orange"
                          }`}
                        >
                          {bom.status || "Draft"}
                        </span>
                        {bom.pr_generated && (
                          <span className="custom-badge status-purple">
                            PR: {bom.generated_pr_voucher || "Generated"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <ReadOnlyInput label="BOM Number" value={bom.bom_no} colClass="col-12 col-md-4 col-xl-3" />
                    <ReadOnlyInput
                      label="Proposal Date"
                      value={bom.date ? moment(bom.date).format("YYYY-MM-DD") : "-"}
                      colClass="col-12 col-md-4 col-xl-3"
                    />
                    <ReadOnlyInput label="Firm / Company" value={bom.firm_id?.name} colClass="col-12 col-md-4 col-xl-3" />
                    <ReadOnlyInput label="Quotation / Inquiry Ref" value={bom.quotation_ref_no} colClass="col-12 col-md-4 col-xl-3" />

                    <ReadOnlyInput
                      label="Client / Customer"
                      value={bom.client_snapshot?.name || bom.client_id?.name}
                      colClass="col-12 col-md-4 col-xl-3"
                    />
                    <ReadOnlyInput
                      label="Attention Person"
                      value={bom.client_snapshot?.attention_person}
                      colClass="col-12 col-md-4 col-xl-3"
                    />
                    <ReadOnlyInput
                      label="Phone / Mobile"
                      value={bom.client_snapshot?.phone}
                      colClass="col-12 col-md-4 col-xl-3"
                    />
                    <ReadOnlyInput
                      label="Client GSTIN"
                      value={bom.client_snapshot?.gst_no}
                      colClass="col-12 col-md-4 col-xl-3"
                    />

                    <ReadOnlyInput
                      label="Project Name"
                      value={bom.project_name || bom.project_id?.name}
                      colClass="col-12 col-md-4 col-xl-4"
                    />
                    <ReadOnlyInput
                      label="Proposal Subject"
                      value={bom.subject}
                      colClass="col-12 col-md-8 col-xl-8"
                    />

                    <ReadOnlyInput
                      label="Client / Site Address"
                      value={clientAddress}
                      colClass="col-12"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Metrics Bar */}
          <div className="row mb-3">
            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <div className="card bg-white p-3 w-100 mb-0 shadow-sm border-0">
                <div className="d-flex align-items-center">
                  <div className="avatar avatar-md bg-light-primary rounded me-3 d-flex align-items-center justify-content-center text-primary">
                    <Layers size={22} />
                  </div>
                  <div>
                    <h6 className="text-muted small mb-1">Panels / Assemblies</h6>
                    <h4 className="mb-0 fw-bold">{metrics.totalAssemblies}</h4>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <div className="card bg-white p-3 w-100 mb-0 shadow-sm border-0">
                <div className="d-flex align-items-center">
                  <div className="avatar avatar-md bg-light-success rounded me-3 d-flex align-items-center justify-content-center text-success">
                    <Boxes size={22} />
                  </div>
                  <div>
                    <h6 className="text-muted small mb-1">Feeders / Sections</h6>
                    <h4 className="mb-0 fw-bold">{metrics.totalFeeders}</h4>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <div className="card bg-white p-3 w-100 mb-0 shadow-sm border-0">
                <div className="d-flex align-items-center">
                  <div className="avatar avatar-md bg-light-warning rounded me-3 d-flex align-items-center justify-content-center text-warning">
                    <Compass size={22} />
                  </div>
                  <div>
                    <h6 className="text-muted small mb-1">BOQ Line Items</h6>
                    <h4 className="mb-0 fw-bold">{metrics.totalItems}</h4>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <div className="card bg-white p-3 w-100 mb-0 shadow-sm border-0">
                <div className="d-flex align-items-center">
                  <div className="avatar avatar-md bg-light-info rounded me-3 d-flex align-items-center justify-content-center text-info">
                    <DollarSign size={22} />
                  </div>
                  <div>
                    <h6 className="text-muted small mb-1">Grand Total (INR)</h6>
                    <h4 className="mb-0 fw-bold text-success">{formatINR(bom.grand_total)}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabbed Detail Sections */}
          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body p-0">
                  <div className="border-bottom px-3 pt-2 bg-light">
                    <Nav
                      variant="tabs"
                      className="modern-tabs border-0 flex-nowrap"
                      activeKey={activeTab}
                      onSelect={(k) => setActiveTab(k)}
                    >
                      <Nav.Item>
                        <Nav.Link
                          eventKey="offer"
                          className="d-flex align-items-center gap-1 py-2 px-3 fw-semibold"
                          style={{ fontSize: "12.5px" }}
                        >
                          <FileSpreadsheet size={15} /> 1. Commercial Offer Summary
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link
                          eventKey="assemblies"
                          className="d-flex align-items-center gap-1 py-2 px-3 fw-semibold"
                          style={{ fontSize: "12.5px" }}
                        >
                          <Layers size={15} /> 2. Hierarchical BOM & Components ({metrics.totalAssemblies})
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link
                          eventKey="specs"
                          className="d-flex align-items-center gap-1 py-2 px-3 fw-semibold"
                          style={{ fontSize: "12.5px" }}
                        >
                          <Sliders size={15} /> 3. Technical Specifications Matrix
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link
                          eventKey="terms"
                          className="d-flex align-items-center gap-1 py-2 px-3 fw-semibold"
                          style={{ fontSize: "12.5px" }}
                        >
                          <FileText size={15} /> 4. Terms & GA Drawing Details
                        </Nav.Link>
                      </Nav.Item>
                    </Nav>
                  </div>

                  <div className="p-3">
                    {/* TAB 1: COMMERCIAL OFFER & ANNEXURE 1 */}
                    {activeTab === "offer" && (
                      <div>
                        <div className="form-heading mb-3">
                          <h4>Annexure 1: Techno-Commercial Scope of Supply</h4>
                        </div>

                        <div className="table-responsive mb-4">
                          <table className="table border-0 mb-0 custom-table table-striped comman-table">
                            <thead>
                              <tr>
                                <th style={{ width: "6%" }} className="text-center">Sr.</th>
                                <th style={{ width: "50%" }}>Assembly / Panel Description</th>
                                <th style={{ width: "14%" }} className="text-center">Quantity</th>
                                <th style={{ width: "15%" }} className="text-end">Unit Rate (₹)</th>
                                <th style={{ width: "15%" }} className="text-end">Total Price (₹)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(bom.assemblies || []).map((ass, idx) => (
                                <tr key={idx}>
                                  <td className="text-center fw-bold">{idx + 1}</td>
                                  <td>
                                    <strong>{ass.assembly_name}</strong>
                                    {ass.assembly_type && (
                                      <span className="badge bg-light text-dark ms-2 border">
                                        {ass.assembly_type}
                                      </span>
                                    )}
                                  </td>
                                  <td className="text-center">
                                    {ass.quantity} {ass.unit || "NOS"}
                                  </td>
                                  <td className="text-end">{formatINR(ass.unit_rate)}</td>
                                  <td className="text-end fw-bold">{formatINR(ass.total_price)}</td>
                                </tr>
                              ))}
                              {(!bom.assemblies || bom.assemblies.length === 0) && (
                                <tr>
                                  <td colSpan="5" className="text-center py-3 text-muted">
                                    No assemblies recorded in this BOM
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        {/* Commercial Breakdown & Ledger Card */}
                        <div className="row g-3">
                          <div className="col-12 col-lg-7">
                            <div className="card shadow-sm border mb-0 h-100">
                              <div className="card-header bg-light py-2 px-3">
                                <h6 className="mb-0 fw-bold text-dark">
                                  Commercial Costing & Statutory Tax Summary
                                </h6>
                              </div>
                              <div className="card-body p-0">
                                <div className="table-responsive">
                                  <table className="table border-0 mb-0 custom-table comman-table">
                                    <tbody>
                                      <tr>
                                        <td className="fw-semibold text-muted">Total Assemblies Quantity</td>
                                        <td className="text-end fw-bold">
                                          {bom.total_assemblies_count || metrics.totalAssemblies} NOS
                                        </td>
                                      </tr>
                                      <tr>
                                        <td className="fw-semibold text-muted">Assemblies Subtotal</td>
                                        <td className="text-end fw-bold">{formatINR(bom.subtotal_amount)}</td>
                                      </tr>
                                      {parseFloat(bom.packaging_charges) > 0 && (
                                        <tr>
                                          <td className="text-muted">
                                            Packaging Charges ({bom.packaging_gst_percent || 18}% GST)
                                          </td>
                                          <td className="text-end">{formatINR(bom.packaging_charges)}</td>
                                        </tr>
                                      )}
                                      {parseFloat(bom.loading_charges) > 0 && (
                                        <tr>
                                          <td className="text-muted">
                                            Loading Charges ({bom.loading_gst_percent || 18}% GST)
                                          </td>
                                          <td className="text-end">{formatINR(bom.loading_charges)}</td>
                                        </tr>
                                      )}
                                      {parseFloat(bom.freight_charges) > 0 && (
                                        <tr>
                                          <td className="text-muted">
                                            Freight Charges ({bom.freight_gst_percent || 18}% GST)
                                          </td>
                                          <td className="text-end">{formatINR(bom.freight_charges)}</td>
                                        </tr>
                                      )}
                                      {parseFloat(bom.other_charges) > 0 && (
                                        <tr>
                                          <td className="text-muted">Other Commercial Charges</td>
                                          <td className="text-end">{formatINR(bom.other_charges)}</td>
                                        </tr>
                                      )}
                                      <tr className="table-light">
                                        <td className="fw-bold">Taxable Amount</td>
                                        <td className="text-end fw-bold">{formatINR(bom.taxable_amount)}</td>
                                      </tr>
                                      <tr>
                                        <td className="text-muted">Total Statutory Tax (GST)</td>
                                        <td className="text-end">{formatINR(bom.total_tax)}</td>
                                      </tr>
                                      <tr className="table-primary">
                                        <td className="fw-bold text-primary fs-6">Grand Total Amount</td>
                                        <td className="text-end fw-bold text-primary fs-6">
                                          {formatINR(bom.grand_total)}
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-12 col-lg-5">
                            <div className="card shadow-sm border mb-0 h-100 d-flex flex-column justify-content-between p-3 bg-light">
                              <div>
                                <h6 className="fw-bold text-primary mb-2">Amount in Words</h6>
                                <p className="lead fw-semibold text-dark mb-3" style={{ fontSize: "14px" }}>
                                  {grandTotalInWords}
                                </p>
                                <hr />
                                <div className="small text-muted mb-2">
                                  <strong>Commercial Note:</strong> Prices are quoted on Ex-Works Surat basis. Statutory taxes and freight are applied as per client commercial agreement.
                                </div>
                              </div>
                              <div className="p-2 bg-white rounded border d-flex align-items-center gap-2">
                                <CheckCircle2 size={20} className="text-success flex-shrink-0" />
                                <div className="small text-muted">
                                  Offer prepared for <strong>{bom.client_snapshot?.name || "Client"}</strong> under quotation reference <strong>{bom.quotation_ref_no || "N/A"}</strong>.
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* TAB 2: HIERARCHICAL BOM & BOQ ITEMS */}
                    {activeTab === "assemblies" && (
                      <div>
                        <div className="page-table-header mb-3">
                          <div className="row align-items-center">
                            <div className="col">
                              <div className="doctor-table-blk">
                                <h3>Annexure 2: Hierarchical Bill of Materials</h3>
                                <div className="doctor-search-blk">
                                  <div className="top-nav-search table-search-blk">
                                    <form onSubmit={(e) => e.preventDefault()}>
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search item, make, feeder..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                      />
                                      <span className="btn">
                                        <SearchIcon size={14} />
                                      </span>
                                    </form>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-auto">
                              <span className="badge bg-light text-dark border py-2 px-3">
                                {metrics.totalAssemblies} Panels | {metrics.totalFeeders} Feeders | {metrics.totalItems} Items
                              </span>
                            </div>
                          </div>
                        </div>

                        {filteredAssemblies.map((assembly, aIdx) => (
                          <div key={aIdx} className="card shadow-sm border mb-4">
                            <div className="card-header bg-light d-flex justify-content-between align-items-center flex-wrap gap-2 py-2 px-3">
                              <div className="d-flex align-items-center gap-2">
                                <span className="badge bg-primary rounded-pill px-2 py-1">
                                  Panel #{assembly.assembly_code || aIdx + 1}
                                </span>
                                <h5 className="mb-0 fw-bold text-dark">{assembly.assembly_name}</h5>
                                {assembly.assembly_type && (
                                  <span className="badge bg-secondary">{assembly.assembly_type}</span>
                                )}
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <span className="badge bg-dark">Board Qty: {assembly.quantity} {assembly.unit || "NOS"}</span>
                                <span className="badge bg-success">Unit Rate: {formatINR(assembly.unit_rate)}</span>
                                <span className="badge bg-primary">Panel Total: {formatINR(assembly.total_price)}</span>
                              </div>
                            </div>

                            <div className="card-body p-3">
                              {(assembly.sub_assemblies || []).map((sub, sIdx) => (
                                <div key={sIdx} className="border rounded mb-3 bg-white overflow-hidden">
                                  <div className="bg-light border-bottom p-2 px-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                      <span className="badge bg-warning text-dark fw-bold">
                                        Feeder {sub.code || String.fromCharCode(65 + sIdx)}
                                      </span>
                                      <strong className="text-dark" style={{ fontSize: "13px" }}>
                                        {sub.name}
                                      </strong>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <span className="badge bg-light text-dark border">
                                        Feeder Qty: {sub.quantity || 1}
                                      </span>
                                      <span className="badge bg-light text-muted border">
                                        {(sub.items || []).length} Components
                                      </span>
                                    </div>
                                  </div>

                                  <div className="table-responsive">
                                    <table className="table border-0 mb-0 custom-table table-striped comman-table">
                                      <thead>
                                        <tr>
                                          <th style={{ width: "5%" }} className="text-center">Sr.</th>
                                          <th style={{ width: "35%" }}>BOQ Item Name & Description</th>
                                          <th style={{ width: "15%" }}>Make / Brand</th>
                                          <th style={{ width: "10%" }} className="text-center">Unit</th>
                                          <th style={{ width: "10%" }} className="text-center">Qty / Board</th>
                                          <th style={{ width: "10%" }} className="text-center">Total Qty</th>
                                          <th style={{ width: "15%" }} className="text-end">Rate (₹)</th>
                                          <th style={{ width: "15%" }} className="text-end">Amount (₹)</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(sub.items || []).map((item, iIdx) => (
                                          <tr key={iIdx}>
                                            <td className="text-center fw-bold">{item.sr_no || iIdx + 1}</td>
                                            <td>
                                              <strong>{item.item_name}</strong>
                                              {item.description && (
                                                <div className="text-muted small" style={{ fontSize: "11px" }}>
                                                  {item.description}
                                                </div>
                                              )}
                                            </td>
                                            <td>{item.make || "-"}</td>
                                            <td className="text-center">{item.unit || "NOS"}</td>
                                            <td className="text-center">{item.unit_quantity}</td>
                                            <td className="text-center fw-bold bg-light">
                                              {item.total_quantity}
                                            </td>
                                            <td className="text-end">{formatINR(item.rate)}</td>
                                            <td className="text-end fw-bold">{formatINR(item.amount)}</td>
                                          </tr>
                                        ))}
                                        {(!sub.items || sub.items.length === 0) && (
                                          <tr>
                                            <td colSpan="8" className="text-center py-2 text-muted">
                                              No components in this feeder
                                            </td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ))}

                              {(!assembly.sub_assemblies || assembly.sub_assemblies.length === 0) && (
                                <div className="text-center py-3 text-muted">
                                  No feeders or sections defined for this assembly
                                </div>
                              )}
                            </div>
                          </div>
                        ))}

                        {filteredAssemblies.length === 0 && (
                          <div className="no-table-data text-center py-5">
                            <p className="text-muted">No components match your search criteria.</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* TAB 3: TECHNICAL SPECIFICATIONS MATRIX */}
                    {activeTab === "specs" && (
                      <div>
                        <div className="form-heading mb-3">
                          <h4>Technical Specifications Matrix</h4>
                        </div>

                        {(bom.assemblies || []).map((assembly, aIdx) => {
                          const specs = assembly.specifications || [];
                          return (
                            <div key={aIdx} className="card shadow-sm border mb-4">
                              <div className="card-header bg-light py-2 px-3 d-flex justify-content-between align-items-center">
                                <h6 className="mb-0 fw-bold text-primary">
                                  Panel #{assembly.assembly_code || aIdx + 1}: {assembly.assembly_name}
                                </h6>
                                <span className="badge bg-secondary">{specs.length} Parameter Groups</span>
                              </div>

                              <div className="card-body p-3">
                                {specs.length > 0 ? (
                                  <div className="row g-3">
                                    {specs.map((group, gIdx) => (
                                      <div className="col-12 col-md-6" key={gIdx}>
                                        <div className="border rounded p-3 h-100 bg-white">
                                          <div className="d-flex align-items-center gap-2 mb-2 pb-2 border-bottom">
                                            <span className="badge bg-light text-primary border fw-bold">
                                              {group.group_code || `G${gIdx + 1}`}
                                            </span>
                                            <strong className="text-dark" style={{ fontSize: "12.5px" }}>
                                              {group.group_title}
                                            </strong>
                                          </div>
                                          <div className="table-responsive">
                                            <table className="table table-sm border-0 mb-0 comman-table" style={{ fontSize: "11px" }}>
                                              <thead>
                                                <tr>
                                                  <th style={{ width: "40%" }}>Parameter</th>
                                                  <th style={{ width: "30%" }}>Description</th>
                                                  <th style={{ width: "30%" }}>Value</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {(group.parameters || []).map((param, pIdx) => (
                                                  <tr key={pIdx}>
                                                    <td className="text-muted">{param.parameter_name}</td>
                                                    <td>{param.details || "-"}</td>
                                                    <td className="fw-semibold text-dark">
                                                      {param.type_value || "-"} {param.unit || ""}
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-center py-4 text-muted small">
                                    No technical specifications defined for this assembly panel.
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* TAB 4: TERMS & CONDITIONS & GA DRAWING DETAILS */}
                    {activeTab === "terms" && (
                      <div>
                        <div className="row g-3">
                          {/* Terms & Conditions */}
                          <div className="col-12 col-lg-7">
                            <div className="card shadow-sm border mb-0 h-100">
                              <div className="card-header bg-light py-2 px-3 d-flex justify-content-between align-items-center">
                                <h6 className="mb-0 fw-bold text-dark">General Terms & Commercial Conditions</h6>
                                {bom.terms_template_name && (
                                  <span className="badge bg-light text-dark border">
                                    {bom.terms_template_name}
                                  </span>
                                )}
                              </div>
                              <div className="card-body p-3">
                                {(bom.terms_and_conditions || []).length > 0 ? (
                                  <div className="table-responsive">
                                    <table className="table border-0 mb-0 custom-table comman-table">
                                      <thead>
                                        <tr>
                                          <th style={{ width: "6%" }} className="text-center">Sr.</th>
                                          <th style={{ width: "30%" }}>Clause</th>
                                          <th style={{ width: "64%" }}>Terms Details</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {bom.terms_and_conditions.map((term, idx) => (
                                          <tr key={idx}>
                                            <td className="text-center fw-bold">{idx + 1}</td>
                                            <td className="fw-bold text-primary">{term.clause_title}</td>
                                            <td>{term.clause_content}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <div className="text-center py-4 text-muted small">
                                    No general terms specified.
                                  </div>
                                )}

                                {bom.custom_terms_rich_text && (
                                  <div className="mt-3 p-3 bg-light rounded border">
                                    <h6 className="fw-bold text-dark mb-1">Additional Terms & Conditions:</h6>
                                    <div
                                      className="small text-muted"
                                      dangerouslySetInnerHTML={{ __html: bom.custom_terms_rich_text }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* GA Drawing Details */}
                          <div className="col-12 col-lg-5">
                            <div className="card shadow-sm border mb-0 h-100">
                              <div className="card-header bg-light py-2 px-3">
                                <h6 className="mb-0 fw-bold text-dark">General Arrangement (GA) Drawing Reference</h6>
                              </div>
                              <div className="card-body p-3">
                                <div className="row g-2 mb-3">
                                  <div className="col-12">
                                    <div className="input-block local-forms">
                                      <label>Drawing No.</label>
                                      <input
                                        className="form-control"
                                        value={bom.drawing_details?.drawing_no || "-"}
                                        readOnly
                                      />
                                    </div>
                                  </div>
                                  <div className="col-12">
                                    <div className="input-block local-forms">
                                      <label>Drawing Title</label>
                                      <input
                                        className="form-control"
                                        value={bom.drawing_details?.title || "-"}
                                        readOnly
                                      />
                                    </div>
                                  </div>
                                  <div className="col-12">
                                    <div className="input-block local-forms">
                                      <label>Revision</label>
                                      <input
                                        className="form-control"
                                        value={bom.drawing_details?.revision || "00"}
                                        readOnly
                                      />
                                    </div>
                                  </div>
                                </div>

                                <div className="card bg-light border p-3 mb-3">
                                  <h6 className="fw-bold text-dark mb-2">Enclosure Dimensions (mm)</h6>
                                  <div className="row text-center g-2">
                                    <div className="col-4">
                                      <div className="bg-white p-2 rounded border">
                                        <div className="small text-muted">Height (H)</div>
                                        <div className="fw-bold fs-6">
                                          {bom.drawing_details?.dimensions?.height || "-"} mm
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-4">
                                      <div className="bg-white p-2 rounded border">
                                        <div className="small text-muted">Width (W)</div>
                                        <div className="fw-bold fs-6">
                                          {bom.drawing_details?.dimensions?.width || "-"} mm
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-4">
                                      <div className="bg-white p-2 rounded border">
                                        <div className="small text-muted">Depth (D)</div>
                                        <div className="fw-bold fs-6">
                                          {bom.drawing_details?.dimensions?.depth || "-"} mm
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="mt-2 text-center">
                                    <span className="badge bg-white text-dark border">
                                      Base Stand Height: {bom.drawing_details?.dimensions?.base_height || "-"} mm
                                    </span>
                                  </div>
                                </div>

                                {bom.drawing_details?.notes && (
                                  <div className="input-block local-forms">
                                    <label>Drawing Notes & Remarks</label>
                                    <textarea
                                      className="form-control"
                                      rows={3}
                                      value={bom.drawing_details.notes}
                                      readOnly
                                      style={{ resize: "none" }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Action Footer */}
          <div className="row mt-3">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => navigate("/admin/bom-management")}
                    >
                      <ArrowLeft size={14} className="me-1" /> Back to BOM List
                    </button>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => navigate("/admin/manage-bom", { state: { id: bom._id } })}
                      >
                        <Edit size={14} className="me-1" /> Edit BOM
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-dark btn-sm"
                        onClick={handleDownloadPdf}
                      >
                        <Download size={14} className="me-1" /> Download PDF
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-success btn-sm"
                        onClick={handleExportExcel}
                      >
                        <FileSpreadsheet size={14} className="me-1" /> Export Excel
                      </button>
                      {!bom.pr_generated && (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={handleGeneratePr}
                        >
                          <Send size={14} className="me-1" /> Generate Store PR
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default ViewBom;
